import { readFileSync, existsSync } from 'fs'
import { parse as parseYaml } from 'yaml'

export interface ModuleBoundary {
  module: string
  paths: string[]
  change_level?: ChangeLevel
}

export interface ContractPolicy {
  allowed: string[]
  forbidden: string[]
  migration_required_when: string[]
}

export interface StateOwnershipPolicy {
  unchanged: string[]
  allowed_new_state: string[]
  forbidden: string[]
}

export interface AutomationProjectionPolicy {
  graphify?: string[]
  openapi_swagger?: string[]
  codegen?: string[]
}

export type ProjectionToolName = 'graphify' | 'openapi_swagger' | 'codegen'

export interface ArchitectureEnvelope {
  version: string
  request_id: string
  legal_layer?: {
    architectural?: Array<{ scope: string; enforcement: string }>
    code?: Array<{ scope: string; enforcement: string }>
  }
  module_boundaries: {
    writable: ModuleBoundary[]
    read_only: ModuleBoundary[]
    forbidden: ModuleBoundary[]
  }
  absorption_budget: {
    max_parallel_slices: number
  }
  /** Conditions under which AI must stop and request human adjudication. */
  escalation_conditions?: string[]
  /** What kinds of contract changes are allowed / forbidden / require migration. */
  contract_policy?: ContractPolicy
  /** State ownership boundaries AI must not cross. */
  state_ownership?: StateOwnershipPolicy
  /** Declared usage scope for automated projection tools (graphify / openapi / codegen). Use is denied by default if not declared. */
  automation_projection_policy?: AutomationProjectionPolicy
}

export type ChangeLevel = 'L0' | 'L1' | 'L2' | 'L3'

export interface DriftResult {
  allowed: string[]
  unexpected: Array<{ file: string; change_level?: ChangeLevel }>
  passed: boolean
}

export interface EnvelopeValidation {
  valid: boolean
  errors: string[]
}

export function loadEnvelope(path: string): ArchitectureEnvelope {
  if (!existsSync(path)) {
    throw new Error(`Envelope file not found: ${path}`)
  }
  const raw = readFileSync(path, 'utf-8')
  const parsed = parseYaml(raw)

  if (!parsed.module_boundaries) {
    throw new Error('Envelope missing required field: module_boundaries')
  }

  return {
    version: parsed.version ?? '1.0',
    request_id: parsed.request_id ?? '',
    legal_layer: parsed.legal_layer,
    module_boundaries: {
      writable: (parsed.module_boundaries.writable ?? []).map((w: any) => ({
        module: w.module,
        paths: w.paths ?? [],
        change_level: w.change_level ?? 'L0',
      })),
      read_only: (parsed.module_boundaries.read_only ?? []).map((r: any) => ({
        module: r.module,
        paths: r.paths ?? [],
      })),
      forbidden: (parsed.module_boundaries.forbidden ?? []).map((f: any) => ({
        module: f.module,
        paths: f.paths ?? [],
      })),
    },
    absorption_budget: {
      max_parallel_slices: parsed.absorption_budget?.max_parallel_slices ?? 1,
    },
    escalation_conditions: parsed.escalation_conditions,
    contract_policy: parsed.contract_policy,
    state_ownership: parsed.state_ownership,
    automation_projection_policy: parsed.automation_projection_policy,
  }
}

export function validateEnvelope(envelope: ArchitectureEnvelope): EnvelopeValidation {
  const errors: string[] = []
  const { writable, read_only, forbidden } = envelope.module_boundaries

  if (writable.length === 0 && read_only.length === 0 && forbidden.length === 0) {
    errors.push('module_boundaries has no entries — at least one writable path is required')
  }

  if (writable.length === 0) {
    errors.push('module_boundaries.writable is empty — deny-by-default requires at least one allowed path')
  }

  const allAllowedPatterns = [...writable, ...read_only].flatMap(b => b.paths)
  const forbiddenPatterns = forbidden.flatMap(b => b.paths)
  for (const fp of forbiddenPatterns) {
    if (allAllowedPatterns.includes(fp)) {
      errors.push(`conflict: path "${fp}" appears in both allowed and forbidden boundaries`)
    }
  }

  // surface declared-but-empty projection tools — non-fatal, but signals intent without scope
  const projPolicy = envelope.automation_projection_policy
  if (projPolicy) {
    for (const tool of ['graphify', 'openapi_swagger', 'codegen'] as const) {
      const declared = projPolicy[tool as keyof AutomationProjectionPolicy]
      if (declared !== undefined && Array.isArray(declared) && declared.length === 0) {
        // These are warnings, not errors. Caller can choose to log them or ignore.
        // We surface them in `errors` so existing callers still see them; severity is encoded in the message.
        errors.push(`warning: automation_projection_policy.${tool} is declared but empty — tool will be denied by default`)
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

export function pathMatchesGlob(filePath: string, pattern: string): boolean {
  const normalized = filePath.replace(/\\/g, '/')
  if (pattern.endsWith('/**')) {
    const prefix = pattern.slice(0, -3)
    return normalized === prefix || normalized.startsWith(prefix + '/')
  }
  if (pattern.includes('*')) {
    const regexStr = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')
      .replace(/\*\*/g, '.*')
      .replace(/\*/g, '[^/]*')
      .replace(/\?/g, '[^/]')
    return new RegExp(`^${regexStr}$`).test(normalized)
  }
  return normalized === pattern || normalized.startsWith(pattern + '/')
}

export function checkDrift(envelope: ArchitectureEnvelope, changedFiles: string[]): DriftResult {
  const allowed: string[] = []
  const unexpected: Array<{ file: string; change_level?: ChangeLevel }> = []

  const writablePatterns = envelope.module_boundaries.writable.flatMap(w => w.paths.map(p => ({ pattern: p, change_level: w.change_level ?? 'L0' as ChangeLevel })))
  const readOnlyPatterns = envelope.module_boundaries.read_only.flatMap(r => r.paths)
  const allAllowedPatterns = [...writablePatterns.map(w => w.pattern), ...readOnlyPatterns]

  for (const file of changedFiles) {
    const matchedWritable = writablePatterns.find(w => pathMatchesGlob(file, w.pattern))
    if (matchedWritable) {
      if (matchedWritable.change_level === 'L2' || matchedWritable.change_level === 'L3') {
        unexpected.push({ file, change_level: matchedWritable.change_level })
      } else {
        allowed.push(file)
      }
    } else if (readOnlyPatterns.some(p => pathMatchesGlob(file, p))) {
      unexpected.push({ file })
    } else if (allAllowedPatterns.length === 0 || !allAllowedPatterns.some(p => pathMatchesGlob(file, p))) {
      unexpected.push({ file })
    } else {
      allowed.push(file)
    }
  }

  return { allowed, unexpected, passed: unexpected.length === 0 }
}

export function getAllowedPaths(envelope: ArchitectureEnvelope, moduleName?: string): string[] {
  const boundaries = [...envelope.module_boundaries.writable, ...envelope.module_boundaries.read_only]
  if (moduleName) {
    return boundaries.filter(b => b.module === moduleName).flatMap(b => b.paths)
  }
  return boundaries.flatMap(b => b.paths)
}

export function getChangeLevel(envelope: ArchitectureEnvelope, path: string): ChangeLevel {
  for (const w of envelope.module_boundaries.writable) {
    if (w.paths.some(p => pathMatchesGlob(path, p))) {
      return w.change_level ?? 'L0'
    }
  }
  for (const r of envelope.module_boundaries.read_only) {
    if (r.paths.some(p => pathMatchesGlob(path, p))) {
      return 'L1'
    }
  }
  for (const f of envelope.module_boundaries.forbidden) {
    if (f.paths.some(p => pathMatchesGlob(path, p))) {
      return 'L3'
    }
  }
  return 'L3'
}

export interface ProjectionUsageResult {
  allowed: boolean
  /** Declared usages that matched the requested usage; empty if the tool is not declared or the usage falls outside declared scope. */
  matchedDeclarations: string[]
  reason: string
}

/**
 * Verify that an automated projection tool usage (graphify / openapi_swagger / codegen) is declared in the envelope's
 * automation_projection_policy. Implements deny-by-default: undeclared tools are not allowed.
 *
 * The matching is intentionally permissive: a request matches if any declared entry contains the request string
 * (substring match), because declared entries are human-readable scopes (e.g. "reverse-extract dependency graph for drift check").
 */
export function checkProjectionToolUsage(
  envelope: ArchitectureEnvelope,
  tool: ProjectionToolName,
  requestedUsage: string,
): ProjectionUsageResult {
  const policy = envelope.automation_projection_policy
  if (!policy) {
    return {
      allowed: false,
      matchedDeclarations: [],
      reason: `automation_projection_policy not declared — tool "${tool}" denied by default`,
    }
  }
  const declared = policy[tool]
  if (!declared || declared.length === 0) {
    return {
      allowed: false,
      matchedDeclarations: [],
      reason: `tool "${tool}" has no declared allowed usages — denied by default`,
    }
  }
  const matched = declared.filter(decl => decl.includes(requestedUsage) || requestedUsage.includes(decl))
  if (matched.length === 0) {
    return {
      allowed: false,
      matchedDeclarations: [],
      reason: `requested usage "${requestedUsage}" not within declared scope for tool "${tool}": ${declared.join('; ')}`,
    }
  }
  return {
    allowed: true,
    matchedDeclarations: matched,
    reason: `matched ${matched.length} declaration(s)`,
  }
}

/**
 * Check whether an escalation condition has been declared in the envelope.
 * AI agents consult this before deciding whether to stop and ask for human adjudication.
 */
export function isEscalationDeclared(envelope: ArchitectureEnvelope, condition: string): boolean {
  if (!envelope.escalation_conditions) return false
  return envelope.escalation_conditions.some(c => c.includes(condition) || condition.includes(c))
}

/**
 * Check whether a proposed state-ownership change is forbidden by the envelope.
 * Used to gate Level 3 changes that would alter state owners.
 */
export function isStateChangeForbidden(envelope: ArchitectureEnvelope, stateName: string): boolean {
  if (!envelope.state_ownership) return false
  return envelope.state_ownership.forbidden.some(s => s.includes(stateName) || stateName.includes(s))
}
